import React, { useMemo, useState } from 'react';
import { ChevronDown, ChevronRight, Loader2, Plus } from 'lucide-react';
import { useUserEstate } from '../hooks/useUserEstate';
import { useUpdateUserEstateRole } from '../hooks/useUserEstate';
import { useUpdateUserEstateStatus } from '../hooks/useUserEstateStatus';
import { useUserTypes } from '../hooks/useUserTypes';
import { roleEditEnabled, type UserEstate } from '../api/userEstate';
import { useT } from '../i18n/admin';
import { UserEstateExpirationDate } from './UserEstateExpirationDate';
import { Modal } from './ui/Modal';
import { Notice } from './ui/Notice';
import { daysUntil, ESTATE_STATUS, estateStatusKind, needsStatusLabel, type EstateStatusKind } from '../utils/userEstateStatus';

interface UserEstateTabProps {
  userId: number;
  /** Cognito group `admin` (rule 14): only they change anything. The server re-checks with a 403. */
  canEdit: boolean;
  onAddEstate: () => void;
}

// Unit | Role | Requested | Expires | Actions — one grid for the header and every row (SAM-973, A1).
const GRID = 'md:grid md:grid-cols-[minmax(0,2fr)_minmax(0,1.6fr)_minmax(0,1.1fr)_minmax(0,2fr)_230px] md:items-center md:gap-4';
const GRID_READ_ONLY = 'md:grid md:grid-cols-[minmax(0,2fr)_minmax(0,1.6fr)_minmax(0,1.1fr)_minmax(0,2fr)] md:items-center md:gap-4';

const SECTION_ORDER: EstateStatusKind[] = ['pending', 'active', 'declined'];
const SECTION_PILL: Record<EstateStatusKind, string> = {
  pending: 'bg-[var(--warn-bg)] text-[var(--warn-fg)]',
  active: 'bg-[var(--ok-bg)] text-[var(--ok-fg)]',
  declined: 'bg-[var(--line-subtle)] text-[var(--ink-700)]',
};

const btn = 'inline-flex h-8 cursor-pointer items-center rounded-lg px-3.5 text-[13px] font-semibold disabled:cursor-not-allowed disabled:opacity-50';
const btnOutline = `${btn} border border-[var(--ink-300)] bg-white text-[var(--ink-700)]`;
const btnPrimary = `${btn} border border-[var(--tenant-primary,#111827)] bg-[var(--tenant-primary,#111827)] text-white`;

const unitLabel = (row: UserEstate) => row.estate?.name || `#${row.estate_id}`;

const byRequestedThenUnit = (a: UserEstate, b: UserEstate) =>
  (Date.parse(b.created_at) || 0) - (Date.parse(a.created_at) || 0) ||
  unitLabel(a).localeCompare(unitLabel(b), undefined, { numeric: true }) ||
  a.estate_id - b.estate_id;

export const UserEstateTab: React.FC<UserEstateTabProps> = ({ userId, canEdit, onAddEstate }) => {
  const { t } = useT();
  const { userEstates, isLoading, isError, refetch } = useUserEstate({ user_id: userId, limit: 100 });
  const { userTypes: allTypes } = useUserTypes();
  // The server only accepts roles the tenant registers (is_register=true; else 400 invalid_user_type).
  const userTypes = useMemo(() => allTypes.filter((u) => u.is_register), [allTypes]);
  const statusMutation = useUpdateUserEstateStatus();
  const roleMutation = useUpdateUserEstateRole();
  const [declinedOpen, setDeclinedOpen] = useState(false);
  const [collapsed, setCollapsed] = useState<Partial<Record<EstateStatusKind, boolean>>>({});
  const [revoking, setRevoking] = useState<UserEstate | null>(null);

  const groups = useMemo(() => {
    const by: Record<EstateStatusKind, UserEstate[]> = { pending: [], active: [], declined: [] };
    for (const row of userEstates) by[estateStatusKind(row.user_estate_status, row.status_id)].push(row);
    // The API gives no stable order and the rows shuffled on every refetch: newest request first, then by unit.
    for (const rows of Object.values(by)) rows.sort(byRequestedThenUnit);
    return by;
  }, [userEstates]);

  const roleEdit = canEdit && roleEditEnabled();
  const busyKey = statusMutation.isPending ? `${statusMutation.variables?.user_id}:${statusMutation.variables?.estate_id}` : null;
  // Approve / Reactivate = Active, Decline = Declined, Revoke = Suspended (reversible; Removed is the user's own doing).
  const targets = { active: ESTATE_STATUS.ACTIVE, declined: ESTATE_STATUS.DECLINED, revoked: ESTATE_STATUS.SUSPENDED };

  const move = (row: UserEstate, statusId: number) => {
    statusMutation.mutate({ user_id: row.user_id, estate_id: row.estate_id, status_id: statusId });
  };

  const renderActions = (row: UserEstate, kind: EstateStatusKind) => {
    const busy = busyKey === `${row.user_id}:${row.estate_id}`;
    if (kind === 'pending') {
      return (
        <>
          <button type="button" className={`${btnOutline} !text-[var(--bad-fg)]`} disabled={busy} onClick={() => move(row, targets.declined)}>
            {t('userEstate.decline')}
          </button>
          <button type="button" className={btnPrimary} disabled={busy} onClick={() => move(row, targets.active)}>
            {t('userEstate.approve')}
          </button>
        </>
      );
    }
    if (kind === 'active' && row.status_id !== ESTATE_STATUS.EXPIRED) {
      return (
        <button type="button" className={`${btn} border border-transparent bg-transparent text-[var(--ink-500)]`} disabled={busy} onClick={() => setRevoking(row)}>
          {t('userEstate.revoke')}
        </button>
      );
    }
    return (
      <button type="button" className={btnOutline} disabled={busy} onClick={() => move(row, targets.active)}>
        {t('userEstate.reactivate')}
      </button>
    );
  };

  const expiryPill = (row: UserEstate) => {
    const days = daysUntil(row.expiration_date);
    if (days == null || days > 30) return null;
    const text = days < 0 ? t('userEstate.expired') : days === 0 ? t('userEstate.expiresToday') : days === 1 ? t('userEstate.expiresIn1') : t('userEstate.expiresIn', { n: days });
    const tone = days < 0 ? 'bg-[var(--bad-bg)] text-[var(--bad-fg)]' : 'bg-[var(--warn-bg)] text-[var(--warn-fg)]';
    return <span className={`st-pill ${tone}`}>{text}</span>;
  };

  const renderRole = (row: UserEstate) => {
    const unit = unitLabel(row);
    const current = row.user_type?.name || t('userEstate.noRole');
    if (!roleEdit) return <span className="text-[13px] text-[var(--ink-900)]">{current}</span>;
    // PUT /user_estate can take ~16 s: while it runs the select keeps the role that was picked, not the old
    // one, and says it is saving.
    const saving = roleMutation.isPending && roleMutation.variables?.user_id === row.user_id && roleMutation.variables?.estate_id === row.estate_id;
    const shownRole = saving ? roleMutation.variables!.user_type_id : row.user_type_id;
    const options = userTypes.some((u) => u.id === shownRole) ? userTypes : [...userTypes, { id: shownRole, name: current } as (typeof userTypes)[number]];
    return (
      <div className="flex items-center gap-2">
        <select
          aria-label={t('userEstate.roleLabel', { unit })}
          aria-busy={saving}
          value={shownRole}
          disabled={roleMutation.isPending}
          onChange={(e) =>
            roleMutation.mutate({ user_id: row.user_id, estate_id: row.estate_id, status_id: row.status_id, user_type_id: Number(e.target.value) })
          }
          className="h-8 w-full min-w-0 max-w-[220px] cursor-pointer rounded-lg border border-[var(--ink-300)] bg-white px-2.5 text-[13px] text-[var(--ink-900)] disabled:cursor-wait disabled:opacity-70 md:min-w-[150px]"
        >
          {options.map((o) => (
            <option key={o.id} value={o.id}>{o.name}</option>
          ))}
        </select>
        {saving && (
          <span role="status" className="inline-flex shrink-0 items-center gap-1.5 text-xs text-[var(--ink-500)]">
            <Loader2 size={14} className="animate-spin" aria-hidden />
            {t('userEstate.savingRole')}
          </span>
        )}
      </div>
    );
  };

  const { fmt } = useT();
  const renderRow = (row: UserEstate, kind: EstateStatusKind, last: boolean) => {
    const unit = unitLabel(row);
    const showActions = canEdit;
    return (
      <li
        key={`${row.user_id}:${row.estate_id}`}
        className={`flex flex-col gap-3 rounded-xl border border-[var(--line)] bg-white p-3.5 md:rounded-none md:border-0 md:px-5 md:py-3 ${showActions ? GRID : GRID_READ_ONLY} ${last ? '' : 'md:border-b md:border-[var(--line-subtle)]'}`}
      >
        <div className="flex items-baseline justify-between gap-3 md:flex-col md:items-start md:gap-0.5">
          <div className="flex min-w-0 flex-col gap-0.5">
            <span className="truncate text-[15px] font-semibold text-black md:text-sm">{unit}</span>
            <span className="truncate text-xs text-[var(--ink-500)]">{row.estate_group?.name || t('userEstate.noGroup')}</span>
            {needsStatusLabel(row.status_id) && row.user_estate_status?.name && (
              <span className="st-pill mt-1 self-start bg-[var(--line-subtle)] text-[var(--ink-700)]">{row.user_estate_status.name}</span>
            )}
          </div>
          <span className="text-xs text-[var(--ink-500)] md:hidden">{row.created_at ? fmt.date(row.created_at) : '—'}</span>
        </div>
        <div className="grid grid-cols-2 gap-2.5 md:contents">
          <div className="flex min-w-0 flex-col gap-1.5 md:block">
            <span className="text-[11px] font-semibold uppercase tracking-wider text-[var(--ink-400)] md:hidden">{t('userEstate.col.role')}</span>
            {renderRole(row)}
          </div>
          <span className="hidden text-[13px] text-[var(--ink-700)] md:inline">{row.created_at ? fmt.date(row.created_at) : '—'}</span>
          <div className="flex min-w-0 flex-col gap-1.5 md:flex-row md:flex-wrap md:items-center md:gap-2">
            <span className="text-[11px] font-semibold uppercase tracking-wider text-[var(--ink-400)] md:hidden">{t('userEstate.col.expires')}</span>
            <div className="flex flex-wrap items-center gap-2">
              <UserEstateExpirationDate userEstate={row} disabled={!canEdit} label={t('userEstate.expiryLabel', { unit })} />
              {expiryPill(row)}
            </div>
          </div>
        </div>
        {showActions && <div className="flex items-center justify-end gap-2">{renderActions(row, kind)}</div>}
      </li>
    );
  };

  const header = (
    <div className={`hidden border-b border-[var(--line)] bg-[var(--surface)] px-5 py-2.5 text-[11px] font-semibold uppercase tracking-wider text-[var(--ink-500)] ${canEdit ? GRID : GRID_READ_ONLY}`}>
      <span>{t('userEstate.col.unit')}</span>
      <span>{t('userEstate.col.role')}</span>
      <span>{t('userEstate.col.requested')}</span>
      <span>{t('userEstate.col.expires')}</span>
      {canEdit && (
        <span className="flex items-center justify-end gap-2">
          {t('userEstate.col.actions')}
          <span className="rounded bg-[#EEF2FF] px-1.5 py-px text-[10px] tracking-wide text-[#3730A3]">{t('userEstate.adminOnly')}</span>
        </span>
      )}
    </div>
  );

  const counts = { active: groups.active.length, pending: groups.pending.length, declined: groups.declined.length };

  return (
    <section className="w-full">
      <div className="mb-5 flex items-center justify-between gap-3">
        <div className="flex flex-col gap-1">
          <h2 className="m-0 text-lg font-semibold text-black">{t('userEstate.title')}</h2>
          <span className="text-[13px] text-[var(--ink-500)]">{t('userEstate.summary', counts)}</span>
        </div>
        {canEdit && (
          <button type="button" className={`${btnPrimary} h-10 gap-2 px-4 text-sm`} onClick={onAddEstate}>
            <Plus size={16} aria-hidden /> {t('userEstate.add')}
          </button>
        )}
      </div>

      {isError ? (
        <Notice variant="error">
          {t('userEstate.loadError')}{' '}
          <button type="button" className="underline" onClick={() => refetch()}>Retry</button>
        </Notice>
      ) : isLoading ? (
        <p className="text-sm text-[var(--ink-500)]">…</p>
      ) : userEstates.length === 0 ? (
        <p className="text-sm text-[var(--ink-500)]">{t('userEstate.empty')}</p>
      ) : (
        SECTION_ORDER.map((kind) => {
          const rows = groups[kind];
          if (rows.length === 0) return null;
          const open = kind === 'declined' ? declinedOpen : !collapsed[kind];
          const toggle = () => (kind === 'declined' ? setDeclinedOpen((o) => !o) : setCollapsed((c) => ({ ...c, [kind]: !c[kind] })));
          return (
            <div key={kind} className="mb-7">
              <button
                type="button"
                aria-expanded={open}
                onClick={toggle}
                className={`mb-2.5 flex cursor-pointer items-center gap-2.5 border-0 bg-transparent p-0 text-[15px] font-semibold ${kind === 'declined' && !open ? 'text-[var(--ink-500)]' : 'text-black'}`}
              >
                {open ? <ChevronDown size={14} aria-hidden /> : <ChevronRight size={14} aria-hidden />}
                {t(`userEstate.section.${kind}` as const)}
                <span className={`st-pill ${SECTION_PILL[kind]}`}>{rows.length}</span>
              </button>
              {open && (
                <div className="md:overflow-visible md:rounded-xl md:border md:border-[var(--line)] md:bg-white">
                  {header}
                  <ul className="m-0 flex list-none flex-col gap-2.5 p-0 md:gap-0">
                    {rows.map((row, i) => renderRow(row, kind, i === rows.length - 1))}
                  </ul>
                </div>
              )}
            </div>
          );
        })
      )}

      <Modal
        isOpen={!!revoking}
        onClose={() => setRevoking(null)}
        title={t('userEstate.revokeTitle')}
        footer={
          <>
            <button type="button" className="btn" onClick={() => setRevoking(null)}>{t('userEstate.back')}</button>
            <button
              type="button"
              className="btn danger"
              onClick={() => {
                if (revoking) move(revoking, targets.revoked);
                setRevoking(null);
              }}
            >
              {t('userEstate.revoke')}
            </button>
          </>
        }
      >
        {revoking && <p className="m-0 text-sm text-[var(--ink-700)]">{t('userEstate.revokeBody', { unit: unitLabel(revoking) })}</p>}
      </Modal>
    </section>
  );
};
